import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, BACKEND_URL } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import ActivityFeed from '@/components/ActivityFeed';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar, Clock, Users, MapPin, Cake, Heart, HeartOff, User, ImageIcon, X, Settings } from 'lucide-react';
import { format, startOfWeek, addDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAuth } from '@/context/AuthContext';

function Dashboard() {
  const [trainings, setTrainings] = useState([]);
  const [users, setUsers] = useState({});
  const [allUsers, setAllUsers] = useState([]);
  const [upcomingBirthdays, setUpcomingBirthdays] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [selectedBirthdayUser, setSelectedBirthdayUser] = useState(null);
  const [backgroundImage, setBackgroundImage] = useState('');
  const [showBgDialog, setShowBgDialog] = useState(false);
  const [bgInputUrl, setBgInputUrl] = useState('');
  const [bgFile, setBgFile] = useState(null);
  const [bgPreview, setBgPreview] = useState(null);
  
  const isPro = user?.es_pro || user?.role === 'entrenador' || user?.role === 'client_plus';
  const [uploadingBg, setUploadingBg] = useState(false);

  useEffect(() => {
    fetchData();
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      if (response.data.calendar_background) {
        const bgUrl = response.data.calendar_background.startsWith('http') 
          ? response.data.calendar_background 
          : `${BACKEND_URL}${response.data.calendar_background}`;
        setBackgroundImage(bgUrl);
      }
    } catch (error) {
      console.error('Error al cargar configuración:', error);
    }
  };

  const fetchData = async () => {
    try {
      const requests = [
        axios.get(`${API}/trainings`),
        user?.role === 'entrenador' ? axios.get(`${API}/users`) : axios.get(`${API}/users/public`),
        user?.role === 'entrenador' ? axios.get(`${API}/users/birthdays/upcoming`) : Promise.resolve({ data: [] })
      ];
      
      const [trainingsRes, usersRes, birthdaysRes] = await Promise.all(requests);
      setTrainings(trainingsRes.data);
      setUpcomingBirthdays(birthdaysRes.data);
      setAllUsers(usersRes.data);
      
      // Crear mapa de usuarios por ID
      const usersMap = {};
      usersRes.data.forEach(u => {
        usersMap[u.id] = u;
      });
      setUsers(usersMap);
    } catch (error) {
      console.error('Error al cargar datos:', error);
    } finally {
      setLoading(false);
    }
  };

  // Obtener cumpleaños para un día específico
  const getBirthdaysForDay = (date) => {
    if (!user?.role === 'entrenador') return [];
    const dayOfMonthToCheck = date.getDate();
    const monthToCheck = date.getMonth() + 1; // getMonth() devuelve 0-11
    return allUsers.filter(u => {
      if (!u.fecha_nacimiento) return false;
      const [, month, dayOfMonth] = u.fecha_nacimiento.split('-');
      return parseInt(month) === monthToCheck && parseInt(dayOfMonth) === dayOfMonthToCheck;
    });
  };

  const fetchTrainings = async () => {
    try {
      const response = await axios.get(`${API}/trainings`);
      setTrainings(response.data);
    } catch (error) {
      console.error('Error al cargar entrenamientos:', error);
    } finally {
      setLoading(false);
    }
  };

  // Contar solo miembros (no entrenadores) para el límite de plazas
  const countMiembros = (training) => {
    if (!training || !training.participantes) return 0;
    return training.participantes.filter(pId => {
      const usuario = users[pId];
      return usuario && usuario.role !== 'entrenador';
    }).length;
  };

  const handleJoinTraining = async (trainingId) => {
    try {
      await axios.post(`${API}/trainings/${trainingId}/join`);
      fetchTrainings();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al apuntarse al entreno');
    }
  };

  const handleLeaveTraining = async (trainingId) => {
    try {
      await axios.delete(`${API}/trainings/${trainingId}/leave`);
      fetchTrainings();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al desapuntarse del entreno');
    }
  };

  const getTrainingsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return trainings.filter((t) => t.fecha === dateStr);
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const getTipoColor = (tipo) => {
    switch (tipo) {
      case 'carrera':
        return { bg: 'bg-blue-600', border: 'border-blue-500', text: 'text-blue-400', badge: 'bg-blue-500' };
      case 'funcional':
        return { bg: 'bg-yellow-600', border: 'border-yellow-500', text: 'text-yellow-400', badge: 'bg-yellow-500' };
      case 'fuerza':
        return { bg: 'bg-amber-600', border: 'border-amber-500', text: 'text-amber-400', badge: 'bg-amber-500' };
      case 'competicion':
        return { bg: 'bg-red-600', border: 'border-red-500', text: 'text-red-400', badge: 'bg-red-500' };
      case 'especial':
        return { bg: 'bg-pink-600', border: 'border-pink-500', text: 'text-pink-400', badge: 'bg-pink-500' };
      case 'carrera_mediciones':
        return { bg: 'bg-purple-600', border: 'border-purple-500', text: 'text-purple-400', badge: 'bg-purple-500' };
      case 'excursion':
        return { bg: 'bg-green-600', border: 'border-green-500', text: 'text-green-400', badge: 'bg-green-500' };
      case 'natacion':
        return { bg: 'bg-cyan-600', border: 'border-cyan-500', text: 'text-cyan-400', badge: 'bg-cyan-500' };
      case 'bike':
        return { bg: 'bg-orange-600', border: 'border-orange-500', text: 'text-orange-400', badge: 'bg-orange-500' };
      default:
        return { bg: 'bg-gray-600', border: 'border-gray-500', text: 'text-gray-400', badge: 'bg-gray-500' };
    }
  };

  const getTipoLabel = (tipo) => {
    switch (tipo) {
      case 'carrera': return 'Carrera';
      case 'funcional': return 'Funcional';
      case 'fuerza': return 'Fuerza';
      case 'competicion': return 'Competicion';
      case 'especial': return 'Especial';
      case 'carrera_mediciones': return 'Carrera + Mediciones';
      case 'excursion': return 'Excursion';
      case 'natacion': return 'Natacion';
      case 'bike': return 'Bike';
      default: return tipo.charAt(0).toUpperCase() + tipo.slice(1);
    }
  };

  // Funciones para imagen de fondo
  const handleBgFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBgFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setBgPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const saveBgImage = async () => {
    setUploadingBg(true);
    try {
      const formData = new FormData();
      
      if (bgFile) {
        formData.append('background_file', bgFile);
      } else if (bgInputUrl) {
        formData.append('background_url', bgInputUrl);
      } else {
        alert('Por favor, selecciona una imagen o introduce una URL');
        setUploadingBg(false);
        return;
      }
      
      const response = await axios.put(`${API}/settings/calendar-background`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const newBgUrl = response.data.calendar_background;
      if (newBgUrl) {
        const fullUrl = newBgUrl.startsWith('http') ? newBgUrl : `${BACKEND_URL}${newBgUrl}`;
        setBackgroundImage(fullUrl);
      }
      
      alert('Imagen de fondo guardada');
      setShowBgDialog(false);
      setBgInputUrl('');
      setBgFile(null);
      setBgPreview(null);
    } catch (error) {
      alert('Error al guardar imagen: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingBg(false);
    }
  };

  const removeBgImage = async () => {
    try {
      await axios.delete(`${API}/settings/calendar-background`);
      setBackgroundImage('');
      setShowBgDialog(false);
      alert('Imagen de fondo eliminada');
    } catch (error) {
      alert('Error al eliminar imagen: ' + error.message);
    }
  };

  // Obtener días del mes
  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Días del mes anterior para llenar
    const startOffset = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;
    for (let i = startOffset; i > 0; i--) {
      const prevDate = new Date(year, month, -i + 1);
      days.push({ date: prevDate, isCurrentMonth: false });
    }
    
    // Días del mes actual
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    // Días del siguiente mes para completar
    const remainingDays = 42 - days.length; // 6 semanas x 7 días
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    
    return days;
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  return (
    <div className="min-h-screen bg-dark-mk">
      <Navbar />
      <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8">
        <div className="mb-4 sm:mb-8">
        {/* Muro movido al final */}

          {/* Header - Responsive */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
            <div>
              <h1 className="text-2xl sm:text-4xl font-bold text-gold" data-testid="dashboard-title">
                Calendario de Entrenamientos
              </h1>
              <p className="text-gold text-sm sm:text-lg italic font-medium mt-1">Aquí empieza el cambio</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {user?.role === 'entrenador' && (
                <Button 
                  onClick={() => setShowBgDialog(true)} 
                  variant="outline" 
                  className="text-white border-gray-600 hover:border-gold text-xs sm:text-sm"
                  size="sm"
                  data-testid="btn-config-bg"
                >
                  <ImageIcon className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Fondo</span>
                </Button>
              )}
              <Button onClick={goToPreviousMonth} className="btn-primary-mk text-xs sm:text-sm" size="sm" data-testid="btn-prev-month">
                ←
              </Button>
              <Button onClick={goToToday} variant="outline" className="text-white border-gold text-xs sm:text-sm" size="sm" data-testid="btn-today">
                Hoy
              </Button>
              <Button onClick={goToNextMonth} className="btn-primary-mk text-xs sm:text-sm" size="sm" data-testid="btn-next-month">
                →
              </Button>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <h2 className="text-xl sm:text-2xl text-white capitalize">
                {format(currentMonth, "MMMM yyyy", { locale: es })}
              </h2>
              <div className="flex items-center gap-2">
                <p className="text-gray-400 text-sm">Bienvenido, {user?.nombre}</p>
                {isPro && (
                  <Badge className="bg-gradient-to-r from-yellow-600 to-yellow-400 text-black font-bold border-0 text-[10px] px-1.5 h-5">
                    PRO
                  </Badge>
                )}
              </div>
            </div>
            
            {/* Alerta compacta de cumpleaños próximos */}
            {user?.role === 'entrenador' && upcomingBirthdays.length > 0 && (
              <div className="flex items-center gap-2 bg-pink-900/30 border border-pink-500/30 rounded-full px-3 py-1.5 self-start sm:self-auto">
                <Cake className="h-4 w-4 text-pink-400" />
                <div className="flex items-center gap-1">
                  {upcomingBirthdays.slice(0, 3).map((birthday, idx) => (
                    <button
                      key={birthday.id}
                      onClick={() => setSelectedBirthdayUser(birthday)}
                      className="flex items-center gap-1 hover:bg-pink-800/30 rounded-full px-2 py-0.5 transition-colors"
                      title={`${birthday.nombre} - ${birthday.dias_restantes === 0 ? '¡Hoy!' : `En ${birthday.dias_restantes} días`}`}
                    >
                      {birthday.foto_perfil ? (
                        <img 
                          src={`${BACKEND_URL}${birthday.foto_perfil}`} 
                          alt={birthday.nombre}
                          className="w-6 h-6 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-gold flex items-center justify-center">
                          <User className="w-3 h-3 text-gray-900" />
                        </div>
                      )}
                      <span className="text-pink-300 text-xs">
                        {birthday.dias_restantes === 0 ? '🎂' : birthday.dias_restantes + 'd'}
                      </span>
                    </button>
                  ))}
                  {upcomingBirthdays.length > 3 && (
                    <span className="text-pink-400 text-xs">+{upcomingBirthdays.length - 3}</span>
                  )}
                </div>
                <button
                  onClick={async () => {
                    try {
                      const res = await axios.post(`${API}/notifications/check-birthdays`);
                      alert(`✅ ${res.data.message}\n🎂 Cumpleaños próximos: ${res.data.cumpleanos_proximos}\n🔔 Notificaciones creadas: ${res.data.notificaciones_creadas}`);
                    } catch (err) {
                      alert('Error al verificar cumpleaños');
                    }
                  }}
                  className="ml-2 text-xs text-pink-400 hover:text-pink-300 underline"
                  title="Crear notificaciones de cumpleaños"
                >
                  🔔
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Modal de detalles del cumpleaños */}
        <Dialog open={!!selectedBirthdayUser} onOpenChange={() => setSelectedBirthdayUser(null)}>
          <DialogContent className="bg-gray-900 text-white border-gray-700 max-w-md">
            {selectedBirthdayUser && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-gold flex items-center gap-2">
                    <Cake className="h-5 w-5 text-pink-400" />
                    Cumpleaños de {selectedBirthdayUser.nombre}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    {selectedBirthdayUser.foto_perfil ? (
                      <img 
                        src={`${BACKEND_URL}${selectedBirthdayUser.foto_perfil}`} 
                        alt={selectedBirthdayUser.nombre}
                        className="w-20 h-20 rounded-full object-cover border-2 border-gold"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-gold flex items-center justify-center">
                        <User className="w-10 h-10 text-gray-900" />
                      </div>
                    )}
                    <div>
                      <h3 className="text-xl font-bold text-white">{selectedBirthdayUser.nombre}</h3>
                      <p className="text-gray-400 text-sm">{selectedBirthdayUser.email}</p>
                      <p className="text-pink-400 font-medium">
                        Cumple {selectedBirthdayUser.edad_cumplira} años
                        {selectedBirthdayUser.dias_restantes === 0 
                          ? ' ¡HOY!' 
                          : selectedBirthdayUser.dias_restantes === 1 
                            ? ' MAÑANA' 
                            : ` el ${format(new Date(selectedBirthdayUser.fecha_cumple), "d 'de' MMMM", { locale: es })}`}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3 pt-4 border-t border-gray-700">
                    <h4 className="text-gray-400 font-medium">Preferencias de entreno</h4>
                    
                    {selectedBirthdayUser.entreno_favorito ? (
                      <div className="bg-green-900/20 border border-green-700/30 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-green-400 text-sm mb-1">
                          <Heart className="h-4 w-4" />
                          Le gusta
                        </div>
                        <p className="text-gray-300 text-sm">{selectedBirthdayUser.entreno_favorito}</p>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm italic">No ha especificado qué le gusta</p>
                    )}

                    {selectedBirthdayUser.entreno_menos_favorito ? (
                      <div className="bg-red-900/20 border border-red-700/30 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-red-400 text-sm mb-1">
                          <HeartOff className="h-4 w-4" />
                          No le gusta
                        </div>
                        <p className="text-gray-300 text-sm">{selectedBirthdayUser.entreno_menos_favorito}</p>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm italic">No ha especificado qué no le gusta</p>
                    )}
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Dialog para configurar fondo del calendario */}
        <Dialog open={showBgDialog} onOpenChange={setShowBgDialog}>
          <DialogContent className="bg-gray-900 text-white border-gray-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-gold flex items-center gap-2">
                <ImageIcon className="h-5 w-5" />
                Configurar Fondo del Calendario
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="bg-url">URL de imagen</Label>
                <Input
                  id="bg-url"
                  value={bgInputUrl}
                  onChange={(e) => setBgInputUrl(e.target.value)}
                  placeholder="https://ejemplo.com/imagen.jpg"
                  className="bg-gray-800 border-gray-700 text-white"
                  data-testid="input-bg-url"
                />
              </div>
              
              <div className="text-center text-gray-500">o</div>
              
              <div className="space-y-2">
                <Label htmlFor="bg-file">Subir imagen</Label>
                <Input
                  id="bg-file"
                  type="file"
                  accept="image/*"
                  onChange={handleBgFileChange}
                  className="bg-gray-800 border-gray-700 text-white"
                  data-testid="input-bg-file"
                />
              </div>
              
              {bgPreview && (
                <div className="relative">
                  <img src={bgPreview} alt="Preview" className="w-full h-32 object-cover rounded-lg" />
                  <button
                    onClick={() => { setBgFile(null); setBgPreview(null); }}
                    className="absolute top-2 right-2 p-1 bg-red-500 rounded-full"
                  >
                    <X className="h-4 w-4 text-white" />
                  </button>
                </div>
              )}
              
              {backgroundImage && !bgPreview && (
                <div className="space-y-2">
                  <Label>Imagen actual</Label>
                  <img src={backgroundImage} alt="Fondo actual" className="w-full h-32 object-cover rounded-lg" />
                </div>
              )}
              
              <div className="flex gap-2">
                <Button 
                  onClick={saveBgImage} 
                  disabled={uploadingBg}
                  className="flex-1 btn-primary-mk"
                  data-testid="btn-save-bg"
                >
                  {uploadingBg ? 'Guardando...' : 'Guardar'}
                </Button>
                {backgroundImage && (
                  <Button 
                    onClick={removeBgImage} 
                    variant="destructive"
                    data-testid="btn-remove-bg"
                  >
                    Quitar
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="loading-spinner"></div>
          </div>
        ) : (
          <div 
            className="rounded-lg p-2 sm:p-4 relative"
            style={{
              backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundColor: backgroundImage ? 'transparent' : 'rgb(17 24 39)'
            }}
            data-testid="calendar-container"
          >
            {/* Overlay oscuro para mejorar legibilidad */}
            {backgroundImage && (
              <div className="absolute inset-0 bg-black/60 rounded-lg"></div>
            )}
            
            <div className="relative z-10">
              {/* Encabezados de días */}
              <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2">
                {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map((day, i) => (
                  <div key={day} className="text-center text-gold font-semibold py-1 sm:py-2 text-xs sm:text-base">
                    <span className="sm:hidden">{day}</span>
                    <span className="hidden sm:inline">{['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'][i]}</span>
                  </div>
                ))}
              </div>

            {/* Días del mes */}
            <div className="grid grid-cols-7 gap-1 sm:gap-2">
              {getDaysInMonth().map((dayInfo, index) => {
                const dayTrainings = getTrainingsForDate(dayInfo.date);
                const isTodayDate = isToday(dayInfo.date);

                return (
                  <div
                    key={index}
                    className={`min-h-[60px] sm:min-h-[120px] border rounded-lg p-1 sm:p-2 ${
                      dayInfo.isCurrentMonth 
                        ? isTodayDate 
                          ? 'bg-gray-800 border-gold border-2' 
                          : 'bg-gray-800 border-gray-700'
                        : 'bg-gray-950 border-gray-800 opacity-50'
                    }`}
                    data-testid={`calendar-day-${format(dayInfo.date, 'yyyy-MM-dd')}`}
                  >
                    <div className={`text-xs sm:text-sm font-semibold mb-1 sm:mb-2 ${
                      dayInfo.isCurrentMonth 
                        ? isTodayDate 
                          ? 'text-gold' 
                          : 'text-white'
                        : 'text-gray-600'
                    }`}>
                      {format(dayInfo.date, 'd')}
                    </div>

                    <div className="space-y-1">
                      {dayTrainings.map((training) => {
                        const colors = getTipoColor(training.tipo);
                        const isParticipating = training.participantes.includes(user?.id);
                        const proHighlight = isPro && isParticipating ? 'ring-1 ring-gold shadow-[0_0_8px_rgba(245,158,11,0.3)]' : '';
                        
                        return (
                          <div
                            key={training.id}
                            className={`rounded p-1 sm:rounded-lg sm:p-2 cursor-pointer transition-all hover:scale-[1.02] ${colors.bg} bg-opacity-20 border-l-2 sm:border-l-4 ${colors.border} ${proHighlight}`}
                            onClick={() => setSelectedTraining(training)}
                            data-testid={`training-card-${training.id}`}
                          >
                            <div className="space-y-0.5 sm:space-y-1">
                              {/* Versión móvil: tipo y hora en dos líneas */}
                              <div className="sm:hidden text-center">
                                <span className={`text-[9px] font-bold px-1 rounded ${colors.badge} text-white block`}>
                                  {training.tipo.substring(0, 3).toUpperCase()}
                                </span>
                                <span className="text-[8px] text-white block mt-0.5">
                                  {training.hora}
                                </span>
                              </div>
                              {/* Versión desktop: completa */}
                              <div className="hidden sm:flex items-center justify-between">
                                <span className={`text-xs font-bold px-2 py-0.5 rounded ${colors.badge} text-white`}>
                                  {getTipoLabel(training.tipo)}
                                </span>
                                <span className="text-xs text-white font-medium" data-testid="training-hora">
                                  {training.hora}
                                </span>
                              </div>

                              <div className="flex items-center text-xs text-gray-300">
                                <Users className="w-3 h-3 mr-1" />
                                <span data-testid="training-participantes">
                                  {countMiembros(training)}/{training.max_participantes}
                                </span>
                              </div>

                              {/* Botones solo visibles en desktop */}
                              <div className="hidden sm:block">
                                {training.plazas_abiertas ? (
                                training.participantes.includes(user?.id) ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className={`w-full text-xs text-white h-6 ${isPro ? 'bg-red-900/40 border-red-500 hover:bg-red-800' : 'bg-red-600 border-red-600 hover:bg-red-700'}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleLeaveTraining(training.id);
                                    }}
                                    data-testid="btn-leave-training"
                                  >
                                    Salir
                                  </Button>
                                ) : countMiembros(training) < training.max_participantes ? (
                                  <Button
                                    size="sm"
                                    className={`w-full text-xs h-6 ${isPro ? 'bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-semibold hover:from-yellow-500 hover:to-yellow-400' : 'btn-primary-mk'}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleJoinTraining(training.id);
                                    }}
                                    data-testid="btn-join-training"
                                  >
                                    Apuntar
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="w-full text-xs h-6"
                                    disabled
                                    data-testid="btn-full-training"
                                  >
                                    Lleno
                                  </Button>
                                )
                              ) : (
                                <div className="text-xs text-gray-400 text-center" data-testid="plazas-cerradas">
                                  Cerrado
                                </div>
                              )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Cumpleaños en este día - Solo para entrenador */}
                    {user?.role === 'entrenador' && getBirthdaysForDay(dayInfo.date).map((birthdayUser) => (
                      <button
                        key={`bday-${birthdayUser.id}`}
                        onClick={() => setSelectedBirthdayUser({
                          ...birthdayUser,
                          dias_restantes: 0,
                          edad_cumplira: new Date().getFullYear() - parseInt(birthdayUser.fecha_nacimiento?.split('-')[0] || 0)
                        })}
                        className="w-full mt-1 flex items-center gap-1 bg-pink-900/30 hover:bg-pink-800/40 border border-pink-500/30 rounded px-2 py-1 transition-colors"
                      >
                        <Cake className="w-3 h-3 text-pink-400 flex-shrink-0" />
                        <span className="text-pink-300 text-xs truncate">{birthdayUser.nombre}</span>
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
            </div>
          </div>
        )}
        
        {/* Dialog para mostrar participantes y apuntarse */}
        <Dialog open={selectedTraining !== null} onOpenChange={() => setSelectedTraining(null)}>
          <DialogContent className="bg-gray-900 text-white border-gray-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-gold flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                {selectedTraining?.tipo && (
                  <Badge className={getTipoColor(selectedTraining.tipo).badge}>
                    {getTipoLabel(selectedTraining.tipo)}
                  </Badge>
                )}
              </DialogTitle>
            </DialogHeader>
            
            {selectedTraining && (
              <div className="space-y-4">
                {/* Info del entreno */}
                <div className="flex items-center gap-4 p-3 bg-gray-800 rounded-lg">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{selectedTraining.fecha?.split('-')[2]}</p>
                    <p className="text-xs text-gray-400 capitalize">
                      {format(new Date(selectedTraining.fecha + 'T00:00:00'), 'MMM', { locale: es })}
                    </p>
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-semibold capitalize">{selectedTraining.dia_semana}</p>
                    <p className="text-gold text-lg flex items-center gap-1">
                      <Clock className="h-4 w-4" /> {selectedTraining.hora}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-400">Plazas</p>
                    <p className={`text-lg font-bold ${countMiembros(selectedTraining) >= selectedTraining.max_participantes ? 'text-red-400' : 'text-green-400'}`}>
                      {countMiembros(selectedTraining)}/{selectedTraining.max_participantes}
                    </p>
                  </div>
                </div>

                {/* Botón de acción */}
                {user?.role === 'entrenador' && (
                  <Button
                    className={`w-full text-xs h-8 mb-2 ${selectedTraining.plazas_abiertas 
                      ? 'bg-red-600/20 text-red-400 border border-red-500/30 hover:bg-red-600/30' 
                      : 'bg-green-600/20 text-green-400 border border-green-500/30 hover:bg-green-600/30'}`}
                    onClick={async () => {
                      try {
                        await axios.patch(`${API}/trainings/${selectedTraining.id}/toggle-plazas`);
                        fetchData();
                        setSelectedTraining(prev => prev ? {...prev, plazas_abiertas: !prev.plazas_abiertas} : null);
                      } catch (e) { alert('Error: ' + e.message); }
                    }}
                    data-testid="btn-toggle-plazas"
                  >
                    {selectedTraining.plazas_abiertas ? 'Cerrar plazas' : 'Abrir plazas'}
                  </Button>
                )}
                {selectedTraining.plazas_abiertas ? (
                  <div className="space-y-2">
                    {selectedTraining.participantes.includes(user?.id) ? (
                      <Button
                        className="w-full bg-red-600 hover:bg-red-700 text-white"
                        onClick={() => {
                          handleLeaveTraining(selectedTraining.id);
                          setSelectedTraining(null);
                        }}
                      >
                        Salir del entreno
                      </Button>
                    ) : countMiembros(selectedTraining) < selectedTraining.max_participantes ? (
                      <Button
                        className="w-full btn-primary-mk"
                        onClick={() => {
                          handleJoinTraining(selectedTraining.id);
                          setSelectedTraining(null);
                        }}
                      >
                        Apuntarme
                      </Button>
                    ) : selectedTraining.lista_espera?.includes(user?.id) ? (
                      <Button
                        variant="outline"
                        className="w-full border-yellow-600 text-yellow-500"
                        onClick={async () => {
                          try {
                            await axios.delete(`${API}/trainings/${selectedTraining.id}/leave-waitlist`);
                            fetchData();
                            setSelectedTraining(null);
                          } catch (error) {
                            alert('Error: ' + error.message);
                          }
                        }}
                      >
                        Salir de lista de espera (posición {(selectedTraining.lista_espera?.indexOf(user?.id) || 0) + 1})
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        className="w-full border-yellow-600 text-yellow-500 hover:bg-yellow-600/20"
                        onClick={() => {
                          handleJoinTraining(selectedTraining.id);
                          setSelectedTraining(null);
                        }}
                      >
                        Unirse a lista de espera
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="text-center p-3 bg-gray-800 rounded-lg">
                    <p className="text-gray-400">Plazas cerradas</p>
                  </div>
                )}

                {/* Lista de participantes */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-400 mb-2 flex items-center gap-2">
                    <Users className="h-4 w-4" /> Participantes ({selectedTraining.participantes.length})
                  </h4>
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {selectedTraining.participantes.length > 0 ? (
                      selectedTraining.participantes.map((userId, index) => (
                        <div key={userId} className="flex items-center justify-between gap-2 p-2 bg-gray-800 rounded-lg">
                          <div className="flex items-center gap-2">
                            {users[userId]?.foto_perfil ? (
                              <img
                                src={`${BACKEND_URL}${users[userId].foto_perfil}`}
                                alt={users[userId]?.nombre}
                                className="w-8 h-8 rounded-full object-cover border border-gold"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center font-bold text-gray-900 text-sm">
                                {users[userId]?.nombre?.charAt(0).toUpperCase() || index + 1}
                              </div>
                            )}
                            <span className="text-white text-sm">{users[userId]?.nombre || 'Usuario'}</span>
                          </div>
                          {user?.role === 'entrenador' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-red-400 hover:text-red-300 hover:bg-red-900/30"
                              onClick={async () => {
                                if (window.confirm(`¿Eliminar a ${users[userId]?.nombre || 'este usuario'} del entreno?`)) {
                                  try {
                                    await axios.delete(`${API}/trainings/${selectedTraining.id}/kick/${userId}`);
                                    fetchTrainings();
                                    setSelectedTraining(prev => ({
                                      ...prev,
                                      participantes: prev.participantes.filter(p => p !== userId)
                                    }));
                                  } catch (error) {
                                    alert('Error al eliminar usuario: ' + (error.response?.data?.detail || error.message));
                                  }
                                }
                              }}
                              data-testid={`kick-user-${userId}`}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 text-sm text-center py-2">No hay participantes aún</p>
                    )}
                  </div>
                </div>

                {/* Lista de espera */}
                {selectedTraining.lista_espera && selectedTraining.lista_espera.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-yellow-500 mb-2">
                      ⏳ Lista de espera ({selectedTraining.lista_espera.length})
                    </h4>
                    <div className="space-y-1">
                      {selectedTraining.lista_espera.map((userId, index) => (
                        <div key={userId} className="flex items-center justify-between gap-2 p-2 bg-yellow-900/20 rounded-lg">
                          <div className="flex items-center gap-2">
                            <span className="text-yellow-500 font-bold text-sm w-6">{index + 1}.</span>
                            <span className="text-white text-sm">{users[userId]?.nombre || 'Usuario'}</span>
                          </div>
                          {user?.role === 'entrenador' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-red-400 hover:text-red-300 hover:bg-red-900/30"
                              onClick={async () => {
                                if (window.confirm(`¿Eliminar a ${users[userId]?.nombre || 'este usuario'} de la lista de espera?`)) {
                                  try {
                                    await axios.delete(`${API}/trainings/${selectedTraining.id}/kick/${userId}`);
                                    fetchTrainings();
                                    setSelectedTraining(prev => ({
                                      ...prev,
                                      lista_espera: prev.lista_espera.filter(p => p !== userId)
                                    }));
                                  } catch (error) {
                                    alert('Error al eliminar usuario: ' + (error.response?.data?.detail || error.message));
                                  }
                                }
                              }}
                              data-testid={`kick-waitlist-${userId}`}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
      {/* Muro de Actividad (Debajo del calendario) */}
      <div className="container mx-auto px-4 pb-8">
        <ActivityFeed />
      </div>
    </div>
  );
}

export default Dashboard;